import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  format, addDays, isSameDay, parseISO,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2, Apple, Copy, Download, Check, CalendarClock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const COLORS = [
  { key: 'blue', dot: 'bg-blue-500', chip: 'bg-blue-100 border-blue-300 text-blue-900' },
  { key: 'amber', dot: 'bg-amber-500', chip: 'bg-amber-100 border-amber-300 text-amber-900' },
  { key: 'emerald', dot: 'bg-emerald-500', chip: 'bg-emerald-100 border-emerald-300 text-emerald-900' },
  { key: 'red', dot: 'bg-red-500', chip: 'bg-red-100 border-red-300 text-red-900' },
  { key: 'purple', dot: 'bg-purple-500', chip: 'bg-purple-100 border-purple-300 text-purple-900' },
];
const colorOf = (k) => COLORS.find((c) => c.key === k) || COLORS[0];
const pad = (n) => String(n).padStart(2, '0');
const emptyForm = (dateStr, hour = 9) => ({
  id: null, summary: '', all_day: false,
  date: dateStr, start_time: `${pad(hour)}:00`, end_time: `${pad(Math.min(hour + 1, 23))}:00`,
  colore: 'blue', description: '', done: false, extra_dates: [],
});

export default function CalendarioNoteWidget() {
  const { user } = useAuth();
  const selfEmail = user?.email || '';
  const [selected, setSelected] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm(format(new Date(), 'yyyy-MM-dd')));
  const [extraDateInput, setExtraDateInput] = useState('');
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo, setRangeTo] = useState('');
  const [skipWeekends, setSkipWeekends] = useState(false);
  const [applyToSeries, setApplyToSeries] = useState(false);
  const [seriesInfo, setSeriesInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [appleOpen, setAppleOpen] = useState(false);
  const [caldav, setCaldav] = useState(null);
  const [reschedEvent, setReschedEvent] = useState(null);
  const [reschedDate, setReschedDate] = useState('');
  const scrollRef = useRef(null);
  // iter103: super-admin può vedere/gestire l'agenda di un altro admin
  const [admins, setAdmins] = useState([]);
  const [isSuper, setIsSuper] = useState(false);
  const [owner, setOwner] = useState(selfEmail);

  useEffect(() => { setOwner((o) => o || selfEmail); }, [selfEmail]);

  useEffect(() => {
    axios.get(`${API}/dashboard/assignable-admins`, { withCredentials: true })
      .then((r) => {
        setIsSuper(!!r.data?.is_super);
        setAdmins(r.data?.items || []);
      })
      .catch(() => { setIsSuper(false); setAdmins([]); });
  }, []);

  const dayKey = format(selected, 'yyyy-MM-dd');
  const viewingOther = owner && selfEmail && owner !== selfEmail;

  const fetchEvents = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dashboard/calendar-events`, {
        params: { date_from: dayKey, date_to: dayKey, owner: owner || selfEmail }, withCredentials: true,
      });
      setEvents((r.data?.items || []).filter((e) => String(e.dtstart || '').slice(0, 10) === dayKey));
    } catch { setEvents([]); }
  }, [dayKey, owner, selfEmail]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 7 * 48;
  }, []);

  const timedByHour = useMemo(() => {
    const map = {};
    for (const e of events) {
      if (e.all_day) continue;
      const h = parseInt(String(e.dtstart || '').slice(11, 13), 10) || 0;
      (map[h] = map[h] || []).push(e);
    }
    return map;
  }, [events]);
  const allDayEvents = events.filter((e) => e.all_day);

  const openNew = (hour) => {
    setExtraDateInput(''); setRangeFrom(''); setRangeTo(''); setSkipWeekends(false); setApplyToSeries(false); setSeriesInfo(null);
    setForm(emptyForm(dayKey, hour)); setDialogOpen(true);
  };
  const openEdit = (e) => {
    setExtraDateInput(''); setRangeFrom(''); setRangeTo(''); setSkipWeekends(false); setApplyToSeries(false); setSeriesInfo(null);
    setForm({
      id: e.id, group_id: e.group_id || null, summary: e.summary || '', all_day: !!e.all_day,
      date: String(e.dtstart || '').slice(0, 10),
      start_time: String(e.dtstart || '').slice(11, 16) || '09:00',
      end_time: String(e.dtend || '').slice(11, 16) || '10:00',
      colore: e.colore || 'blue', description: e.description || '', done: !!e.done,
    });
    setDialogOpen(true);
    if (e.group_id) {
      axios.get(`${API}/dashboard/calendar-events/group/${e.group_id}/count`, { withCredentials: true })
        .then((r) => setSeriesInfo(r.data)).catch(() => setSeriesInfo(null));
    }
  };

  const toggleDone = async (e, ev) => {
    ev.stopPropagation();
    try {
      await axios.put(`${API}/dashboard/calendar-events/${e.id}`, { done: !e.done }, { withCredentials: true });
      fetchEvents();
    } catch { toast.error('Errore'); }
  };

  // Rinvia: sposta l'evento a un'altra data mantenendo l'orario
  const openReschedule = (e, ev) => {
    ev.stopPropagation();
    setReschedEvent(e);
    setReschedDate(format(addDays(parseISO(String(e.dtstart).slice(0, 10)), 1), 'yyyy-MM-dd'));
  };
  const doReschedule = async (newDateStr) => {
    const e = reschedEvent;
    if (!e || !newDateStr) return;
    const payload = e.all_day
      ? { dtstart: newDateStr, dtend: newDateStr }
      : { dtstart: `${newDateStr}${String(e.dtstart).slice(10) || 'T09:00:00'}`, dtend: `${newDateStr}${String(e.dtend).slice(10) || 'T10:00:00'}` };
    try {
      await axios.put(`${API}/dashboard/calendar-events/${e.id}`, payload, { withCredentials: true });
      setReschedEvent(null);
      await fetchEvents();
      toast.success(`Rinviato a ${format(parseISO(newDateStr), 'EEEE d MMMM', { locale: it })}`);
    } catch { toast.error('Errore nel rinvio'); }
  };

  const save = async () => {
    if (!form.summary.trim()) { toast.error('Inserisci un titolo'); return; }
    setSaving(true);
    const base = { colore: form.colore, description: form.description, done: form.done };
    const payload = form.all_day
      ? { ...base, summary: form.summary.trim(), all_day: true, dtstart: form.date, dtend: form.date }
      : { ...base, summary: form.summary.trim(), all_day: false, dtstart: `${form.date}T${form.start_time}:00`, dtend: `${form.date}T${form.end_time}:00` };
    if (!form.id && isSuper && owner && owner !== selfEmail) payload.assignee_email = owner;
    // include anche una data digitata nell'input ma non ancora "Aggiunta"
    const pending = (!form.id && extraDateInput) ? [extraDateInput] : [];
    const extra = !form.id
      ? [...new Set([...(form.extra_dates || []), ...pending])].filter((d) => d && d !== form.date)
      : [];
    if (extra.length) payload.extra_dates = extra;
    try {
      if (form.id) {
        if (form.group_id && applyToSeries) {
          const gp = { summary: form.summary.trim(), description: form.description, colore: form.colore, all_day: form.all_day };
          if (!form.all_day) { gp.start_time = form.start_time; gp.end_time = form.end_time; }
          await axios.put(`${API}/dashboard/calendar-events/group/${form.group_id}`, gp, { withCredentials: true });
        } else {
          await axios.put(`${API}/dashboard/calendar-events/${form.id}`, payload, { withCredentials: true });
        }
      } else {
        await axios.post(`${API}/dashboard/calendar-events`, payload, { withCredentials: true });
      }
      setDialogOpen(false);
      await fetchEvents();
      const total = 1 + extra.length;
      if (form.id && form.group_id && applyToSeries) toast.success(`Serie aggiornata${seriesInfo?.count ? ` (${seriesInfo.count} date)` : ''}`);
      else toast.success(form.id ? 'Evento aggiornato' : (total > 1 ? `Evento creato su ${total} date` : 'Evento creato'));
    } catch { toast.error('Errore nel salvataggio'); } finally { setSaving(false); }
  };

  // iter108: genera tutte le date di un intervallo "dal–al" e le aggiunge alle altre date
  const addRange = () => {
    if (!rangeFrom || !rangeTo) return;
    let a = parseISO(rangeFrom); let b = parseISO(rangeTo);
    if (a > b) { const t = a; a = b; b = t; }
    const out = []; let cur = a; let guard = 0;
    while (cur <= b && guard < 400) {
      const dow = cur.getDay();
      if (!(skipWeekends && (dow === 0 || dow === 6))) out.push(format(cur, 'yyyy-MM-dd'));
      cur = addDays(cur, 1); guard += 1;
    }
    const merged = [...new Set([...(form.extra_dates || []), ...out])].filter((d) => d && d !== form.date).sort();
    setForm({ ...form, extra_dates: merged });
    setRangeFrom(''); setRangeTo('');
  };

  // iter107: gestione date aggiuntive per inserimento multiplo (solo in creazione)
  const addExtraDate = () => {
    const d = extraDateInput;
    if (!d) return;
    if (d === form.date || (form.extra_dates || []).includes(d)) { setExtraDateInput(''); return; }
    setForm({ ...form, extra_dates: [...(form.extra_dates || []), d].sort() });
    setExtraDateInput('');
  };
  const removeExtraDate = (d) => setForm({ ...form, extra_dates: (form.extra_dates || []).filter((x) => x !== d) });

  const remove = async () => {
    if (!form.id) return;
    const series = !!(form.group_id && applyToSeries);
    if (series && !window.confirm(`Eliminare TUTTE le ${seriesInfo?.count || ''} date di questa serie?`)) return;
    try {
      if (series) await axios.delete(`${API}/dashboard/calendar-events/group/${form.group_id}`, { withCredentials: true });
      else await axios.delete(`${API}/dashboard/calendar-events/${form.id}`, { withCredentials: true });
      setDialogOpen(false);
      await fetchEvents();
      toast.success(series ? 'Serie eliminata' : 'Evento eliminato');
    } catch { toast.error('Errore eliminazione'); }
  };

  const loadCaldav = async () => {
    if (caldav) return;
    try {
      const r = await axios.get(`${API}/dashboard/caldav-info`, { withCredentials: true });
      setCaldav(r.data);
    } catch { /* ignore */ }
  };
  const toggleApple = () => { const n = !appleOpen; setAppleOpen(n); if (n) loadCaldav(); };

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const feedHttps = caldav ? origin + caldav.feed_path : '';
  const feedWebcal = feedHttps.replace(/^https?:\/\//, 'webcal://');
  const caldavServer = caldav ? origin + caldav.caldav_path : '';
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success('Copiato'); };

  return (
    <Card className="border-slate-200" data-testid="calendario-note-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-blue-600" /> Agenda
          </CardTitle>
          <div className="flex items-center gap-1">
            <button onClick={() => setSelected(addDays(selected, -1))} className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid="agenda-prev-day" aria-label="Giorno precedente"><ChevronLeft className="h-4 w-4" /></button>
            <input
              type="date"
              value={dayKey}
              onChange={(e) => { if (e.target.value) setSelected(parseISO(e.target.value)); }}
              className="text-xs border border-slate-200 rounded px-2 py-1"
              data-testid="agenda-date-input"
            />
            <button onClick={() => setSelected(addDays(selected, 1))} className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid="agenda-next-day" aria-label="Giorno successivo"><ChevronRight className="h-4 w-4" /></button>
            <Button size="sm" variant="outline" className="h-7 text-xs ml-1" onClick={() => setSelected(new Date())} data-testid="agenda-today">Oggi</Button>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <p className="text-xs text-slate-500 capitalize" data-testid="agenda-day-label">
            {format(selected, 'EEEE d MMMM yyyy', { locale: it })}
            {isSameDay(selected, new Date()) && <span className="ml-1 text-blue-600 font-medium">· oggi</span>}
          </p>
          <button onClick={toggleApple} className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800" data-testid="agenda-apple-toggle">
            <Apple className="h-3.5 w-3.5" /> Calendario Apple
          </button>
        </div>
        {isSuper && admins.length > 1 && (
          <div className="flex items-center gap-2 mt-1" data-testid="agenda-owner-selector">
            <span className="text-[11px] text-slate-500 whitespace-nowrap">Agenda di:</span>
            <Select value={owner} onValueChange={setOwner}>
              <SelectTrigger className="h-7 text-xs flex-1" data-testid="agenda-owner-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {admins.map((a) => (
                  <SelectItem key={a.email} value={a.email} data-testid={`agenda-owner-${a.email}`}>
                    {a.name}{a.self ? ' (tu)' : ''} — {a.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {viewingOther && <span className="text-[10px] text-amber-600 font-medium whitespace-nowrap">assegni a {admins.find((a) => a.email === owner)?.name}</span>}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-1">
        {appleOpen && (
          <div className="mb-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs space-y-2" data-testid="agenda-apple-panel">
            <p className="font-semibold text-slate-700 flex items-center gap-1"><Apple className="h-3.5 w-3.5" /> Collega il Calendario Apple</p>
            <div>
              <p className="text-slate-500 mb-1">Abbonamento (sola lettura, consigliato su iPhone):</p>
              <div className="flex items-center gap-1">
                <a href={feedWebcal} className="flex-1 truncate text-blue-600 underline" data-testid="agenda-feed-link">{feedWebcal || '…'}</a>
                <button onClick={() => copy(feedHttps)} className="p-1 rounded hover:bg-slate-200" title="Copia URL https" data-testid="agenda-feed-copy"><Copy className="h-3.5 w-3.5" /></button>
              </div>
              <p className="text-slate-400 mt-0.5">iPhone: Impostazioni → Calendario → Account → Aggiungi account → Altro → Aggiungi calendario con abbonamento.</p>
            </div>
            <div className="border-t border-slate-200 pt-2">
              <p className="text-slate-500 mb-1">CalDAV bidirezionale (richiede dominio che consenta WebDAV):</p>
              <div className="font-mono text-[10px] text-slate-600 break-all">Server: {caldavServer || '…'}</div>
              <div className="font-mono text-[10px] text-slate-600">Utente: {caldav?.username || '…'}</div>
              <div className="font-mono text-[10px] text-slate-600 flex items-center gap-1">Password: {caldav?.password || '…'}
                {caldav?.password && <button onClick={() => copy(caldav.password)} className="p-0.5 rounded hover:bg-slate-200"><Copy className="h-3 w-3" /></button>}
              </div>
            </div>
          </div>
        )}

        {allDayEvents.length > 0 && (
          <div className="mb-2 space-y-1" data-testid="agenda-allday">
            {allDayEvents.map((e) => (
              <div key={e.id} className={`flex items-center gap-2 text-xs px-2 py-1 rounded border ${colorOf(e.colore).chip} ${e.done ? 'opacity-60' : ''}`} data-testid={`agenda-event-${e.id}`}>
                <button onClick={(ev) => toggleDone(e, ev)} data-testid={`agenda-done-${e.id}`} title={e.done ? 'Segna da fare' : 'Segna come svolto'} className="shrink-0">
                  <span className={`h-4 w-4 rounded border flex items-center justify-center ${e.done ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300'}`}>{e.done && <Check className="h-3 w-3 text-white" />}</span>
                </button>
                <button onClick={() => openEdit(e)} className={`flex-1 text-left font-medium ${e.done ? 'line-through' : ''}`}>Tutto il giorno · {e.summary}</button>
                <button onClick={(ev) => openReschedule(e, ev)} data-testid={`agenda-reschedule-${e.id}`} title="Rinvia ad un altro giorno" className="shrink-0 text-slate-500 hover:text-blue-600"><CalendarClock className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}

        <div ref={scrollRef} className="max-h-[360px] overflow-y-auto border border-slate-100 rounded-lg">
          {HOURS.map((h) => (
            <div key={h} className="flex items-stretch border-b border-slate-50 last:border-0 min-h-[48px]" data-testid={`agenda-hour-${pad(h)}`}>
              <button
                onClick={() => openNew(h)}
                className="w-14 shrink-0 text-[11px] text-slate-400 pt-1 text-right pr-2 hover:bg-blue-50/50"
                data-testid={`agenda-hour-add-${pad(h)}`}
                title="Aggiungi evento"
              >{pad(h)}:00</button>
              <div className="flex-1 p-1 space-y-1 hover:bg-slate-50/50 cursor-pointer" onClick={() => openNew(h)}>
                {(timedByHour[h] || []).map((e) => (
                  <div
                    key={e.id}
                    className={`flex items-center gap-2 text-xs px-2 py-1 rounded border ${colorOf(e.colore).chip} ${e.done ? 'opacity-60' : ''}`}
                    data-testid={`agenda-event-${e.id}`}
                  >
                    <button onClick={(ev) => toggleDone(e, ev)} data-testid={`agenda-done-${e.id}`} title={e.done ? 'Segna da fare' : 'Segna come svolto'} className="shrink-0">
                      <span className={`h-4 w-4 rounded border flex items-center justify-center ${e.done ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300'}`}>{e.done && <Check className="h-3 w-3 text-white" />}</span>
                    </button>
                    <button onClick={(ev) => { ev.stopPropagation(); openEdit(e); }} className={`flex-1 text-left ${e.done ? 'line-through' : ''}`}>
                      <span className="font-semibold">{String(e.dtstart).slice(11, 16)}</span> {e.summary}
                    </button>
                    <button onClick={(ev) => openReschedule(e, ev)} data-testid={`agenda-reschedule-${e.id}`} title="Rinvia ad un altro giorno" className="shrink-0 text-slate-500 hover:text-blue-600"><CalendarClock className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button onClick={() => openNew(9)} size="sm" className="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="agenda-new-event">
          <Plus className="h-4 w-4 mr-1" /> Nuovo evento
        </Button>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="agenda-event-dialog">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Modifica evento' : 'Nuovo evento'}</DialogTitle>
            <DialogDescription>Evento/promemoria dell&apos;agenda — sincronizzato col feed Apple</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} placeholder="Titolo (es. Sopralluogo cantiere Rossi)" data-testid="agenda-form-summary" autoFocus />
            <div className="flex items-center gap-2">
              <Switch checked={form.all_day} onCheckedChange={(v) => setForm({ ...form, all_day: v })} data-testid="agenda-form-allday" />
              <span className="text-sm text-slate-600">Tutto il giorno</span>
            </div>
            {form.id && (
              <div className="flex items-center gap-2">
                <Switch checked={form.done} onCheckedChange={(v) => setForm({ ...form, done: v })} data-testid="agenda-form-done" />
                <span className="text-sm text-slate-600">Svolto {form.done && <span className="text-emerald-600">✔</span>}</span>
              </div>
            )}
            {form.id && form.group_id && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-2" data-testid="agenda-form-series-box">
                <div className="flex items-center gap-2">
                  <Switch checked={applyToSeries} onCheckedChange={setApplyToSeries} data-testid="agenda-form-series-toggle" />
                  <span className="text-sm text-blue-800">Applica a tutta la serie{seriesInfo?.count ? ` (${seriesInfo.count} date)` : ''}</span>
                </div>
                <p className="text-[11px] text-blue-600 mt-1">Modifica (titolo, orario, colore) o eliminazione verranno applicate a tutte le date create insieme. Ogni data resta invariata.</p>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-3">
                <label className="text-xs text-slate-500">Data</label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="agenda-form-date" disabled={!!(form.group_id && applyToSeries)} />
                {form.group_id && applyToSeries && <p className="text-[11px] text-blue-600 mt-0.5">Con la serie attiva, ogni data resta invariata.</p>}
              </div>
              {!form.id && (
                <div className="col-span-3">
                  <label className="text-xs text-slate-500">Altre date (stesso orario)</label>
                  <div className="flex gap-2">
                    <Input type="date" value={extraDateInput} onChange={(e) => setExtraDateInput(e.target.value)} data-testid="agenda-form-extra-date-input" />
                    <Button type="button" variant="outline" size="sm" onClick={addExtraDate} data-testid="agenda-form-add-date">Aggiungi</Button>
                  </div>
                  <div className="flex gap-2 mt-2 items-end">
                    <div className="flex-1">
                      <label className="text-[11px] text-slate-500">Dal</label>
                      <Input type="date" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} data-testid="agenda-form-range-from" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[11px] text-slate-500">Al</label>
                      <Input type="date" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} data-testid="agenda-form-range-to" />
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addRange} data-testid="agenda-form-add-range">Intervallo</Button>
                  </div>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <Switch checked={skipWeekends} onCheckedChange={setSkipWeekends} data-testid="agenda-form-skip-weekends" />
                    <span className="text-xs text-slate-600">Solo giorni feriali (escludi sab/dom)</span>
                  </label>
                  {(form.extra_dates || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2" data-testid="agenda-form-extra-dates">
                      {(form.extra_dates || []).map((d) => (
                        <span key={d} className="inline-flex items-center gap-1 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded px-2 py-0.5" data-testid={`agenda-extra-date-${d}`}>
                          {format(parseISO(d), 'd MMM yyyy', { locale: it })}
                          <button type="button" onClick={() => removeExtraDate(d)} className="text-blue-500 hover:text-red-600 font-bold leading-none" aria-label="Rimuovi data">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-slate-400 mt-1">L&apos;evento verrà creato anche in queste date, con lo stesso orario e titolo.</p>
                </div>
              )}
              {!form.all_day && (
                <>
                  <div>
                    <label className="text-xs text-slate-500">Inizio</label>
                    <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="agenda-form-start" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Fine</label>
                    <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="agenda-form-end" />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Colore</span>
              {COLORS.map((c) => (
                <button key={c.key} onClick={() => setForm({ ...form, colore: c.key })} className={`h-5 w-5 rounded-full ${c.dot} ${form.colore === c.key ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`} data-testid={`agenda-color-${c.key}`} aria-label={c.key} />
              ))}
            </div>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Note (facoltative)" rows={2} data-testid="agenda-form-desc" />
          </div>
          <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
            <div className="flex gap-1">
              {form.id && (
                <>
                  <Button variant="outline" size="sm" onClick={remove} className="text-red-600 border-red-200" data-testid="agenda-form-delete"><Trash2 className="h-4 w-4" /></Button>
                  <a href={`${API}/dashboard/calendar-events/${form.id}.ics`} data-testid="agenda-form-ics"><Button variant="outline" size="sm" title="Scarica .ics per Apple Calendar"><Download className="h-4 w-4" /></Button></a>
                </>
              )}
            </div>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="agenda-form-save">{form.id ? 'Salva' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reschedEvent} onOpenChange={(o) => { if (!o) setReschedEvent(null); }}>
        <DialogContent className="max-w-xs" data-testid="agenda-reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Rinvia evento</DialogTitle>
            <DialogDescription className="truncate">{reschedEvent?.summary}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" data-testid="reschedule-tomorrow" onClick={() => doReschedule(format(addDays(new Date(), 1), 'yyyy-MM-dd'))}>Domani</Button>
              <Button variant="outline" size="sm" data-testid="reschedule-week" onClick={() => doReschedule(format(addDays(new Date(), 7), 'yyyy-MM-dd'))}>Tra 7 giorni</Button>
            </div>
            <div>
              <label className="text-xs text-slate-500">Scegli una data</label>
              <Input type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} data-testid="reschedule-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => doReschedule(reschedDate)} disabled={!reschedDate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="reschedule-confirm">Sposta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
